import React, { useEffect, useState, useMemo } from 'react';
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchTimeBalances } from '../../../redux/slices/timeBalanceSlice';
import { useTranslation } from 'react-i18next'; 

const minutesToHM = (minutes) => {
    const h = Math.floor(Math.abs(minutes) / 60);
    const m = Math.abs(minutes) % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
};

const getWeekRange = (baseDate) => {
    const d = new Date(baseDate);
    const day = d.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(d.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { monday, sunday };
};

const TABS = [
    { key: 'week',  labelKey: 'components.WorkedHours.tabWeek' },
    { key: 'month', labelKey: 'components.WorkedHours.tabMonth' },
];

const WorkedHours = () => {
    const { t, i18n } = useTranslation(); 
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.auth.user);
    const { isLoading } = useSelector(state => state.timeBalances);

    const [activeTab, setActiveTab] = useState('week');
    const [displayItems, setDisplayItems] = useState([]);

    const [periodOffset, setPeriodOffset] = useState(0);

    useEffect(() => {
        setPeriodOffset(0);
    }, [activeTab]);

    const getTargetDate = useMemo(() => {
        const d = new Date();
        if (activeTab === 'week') {
            d.setDate(d.getDate() + (periodOffset * 7));
        } else if (activeTab === 'month') {
            d.setMonth(d.getMonth() + periodOffset);
        }
        return d;
    }, [activeTab, periodOffset]);

    useEffect(() => {
        if (!currentUser?.id) return;

        const targetDate = getTargetDate;
        
        const { monday } = getWeekRange(targetDate);
        const refDate = activeTab === 'week' ? monday : targetDate;

        const params = {
            userId: currentUser.id,
            year: refDate.getFullYear(),
            month: refDate.getMonth() + 1,
        };

        dispatch(fetchTimeBalances(params))
            .unwrap()
            .then(result => {
                if (activeTab === 'week') {
                    const { monday, sunday } = getWeekRange(targetDate);
                    setDisplayItems(result.filter(b => {
                        const [y, mo, d] = b.date.split('-').map(Number);
                        const date = new Date(y, mo - 1, d);
                        return date >= monday && date <= sunday;
                    }));
                } else {
                    setDisplayItems(result);
                }
            })
            .catch(() => setDisplayItems([]));

    }, [activeTab, periodOffset, getTargetDate, currentUser?.id, dispatch]);

    const { totalWorked, totalExpected } = useMemo(() => {
        const worked = displayItems.reduce((s, b) => s + (b.worked_minutes || 0), 0);

        const getBusinessDays = (startDate, endDate) => {
            let count = 0;
            let curDate = new Date(startDate);
            curDate.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);

            while (curDate <= end) {
                const dayOfWeek = curDate.getDay();
                if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
                curDate.setDate(curDate.getDate() + 1);
            }
            return count;
        };

        let expectedMinutes = 0;
        const targetDate = getTargetDate;

        if (activeTab === 'week') {
            const { monday, sunday } = getWeekRange(targetDate);
            expectedMinutes = getBusinessDays(monday, sunday) * 480; 
        } 
        else if (activeTab === 'month') {
            const firstDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
            const lastDay = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
            expectedMinutes = getBusinessDays(firstDay, lastDay) * 480;
        } 

        return {
            totalWorked: worked,
            totalExpected: expectedMinutes,
        };
    }, [displayItems, activeTab, getTargetDate]);

    const percentage = totalExpected > 0
        ? Math.min((totalWorked / totalExpected) * 100, 100)
        : 0;

    const diff     = totalWorked - totalExpected;
    const isAhead  = diff >= 0;
    const diffLabel = `${isAhead ? '+' : '-'}${minutesToHM(Math.abs(diff))}`;

    const periodLabel = useMemo(() => {
        const currentLocale = i18n.language && i18n.language.startsWith('en') ? 'en-US' : 'pt-PT';
        const fmt = d => d.toLocaleDateString(currentLocale, { day: 'numeric', month: 'short' });
        const targetDate = getTargetDate;
        
        if (activeTab === 'week') {
            const { monday, sunday } = getWeekRange(targetDate);
            return `${fmt(monday)} - ${fmt(sunday)}`;
        }
        if (activeTab === 'month') {
            const monthStr = targetDate.toLocaleString(currentLocale, { month: 'long', year: 'numeric' });
            return monthStr.charAt(0).toUpperCase() + (currentLocale === 'pt-PT' ? monthStr.slice(1).replace(' de ', ' ') : monthStr.slice(1));
        }
    }, [activeTab, getTargetDate, i18n.language]);

    return (
        <div className="atlar-card p-4 w-100">
            
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="txt-main m-0 fw-bold">{t('components.WorkedHours.title')}</h3>
                <p className="txt-brand fw-bold text-decoration-none small">
                    {t('components.WorkedHours.goodJob')}
                </p>
            </div>

            <div className="d-flex gap-2 mb-3">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className="border-0 px-3 py-1 fw-semibold small"
                        style={{
                            borderRadius: '50rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            backgroundColor: activeTab === tab.key
                                ? 'var(--brand-primary)'
                                : 'var(--gray-100)',
                            color: activeTab === tab.key
                                ? 'var(--white)'
                                : 'var(--text-secondary)',
                        }}
                    >
                        {t(tab.labelKey)}
                    </button>
                ))}
            </div>

            <div className="d-flex align-items-center gap-3 mb-4">
                <button 
                    onClick={() => setPeriodOffset(prev => prev - 1)}
                    className="border-0 bg-transparent p-0 d-flex align-items-center"
                    style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
                >
                    <ChevronLeft size={20} />
                </button>
                
                <p className="txt-subtle small m-0 fw-bold text-center" style={{ minWidth: "130px", textTransform: 'capitalize' }}>
                    {periodLabel}
                </p>

                <button 
                    onClick={() => setPeriodOffset(prev => prev + 1)}
                    disabled={periodOffset >= 0}
                    className="border-0 bg-transparent p-0 d-flex align-items-center"
                    style={{ 
                        cursor: periodOffset >= 0 ? 'default' : 'pointer', 
                        color: periodOffset >= 0 ? 'var(--gray-300)' : 'var(--text-secondary)' 
                    }}
                >
                    <ChevronRight size={20} />
                </button>
            </div>

            <div className="d-flex gap-4 mb-3">
                <div className="d-flex align-items-center gap-2">
                    <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'var(--brand-primary)' }} />
                    <span className="txt-main fw-bold small">{t('components.WorkedHours.worked')}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'var(--gray-200)' }} />
                    <span className="txt-subtle fw-semibold small">{t('components.WorkedHours.toComplete')}</span>
                </div>
            </div>

            {isLoading ? (
                <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" style={{ color: 'var(--brand-primary)' }} />
                </div>
            ) : (
                <>

                    <div 
                        className="w-100 d-flex mb-3 align-items-center" 
                        style={{ 
                            height: '28px', 
                            borderRadius: 'var(--btn-radius)', 
                            backgroundColor: 'var(--gray-200)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <div 
                            style={{ 
                                width: `${percentage}%`, 
                                height: '100%',
                                backgroundColor: 'var(--brand-primary)',
                                transition: 'width 0.6s ease-out',
                                flexShrink: 0
                            }}
                        />
                        
                        {percentage < 100 && (
                            <div 
                                className="ps-2 txt-subtle small fw-bold" 
                                style={{ 
                                    fontSize: '0.75rem',
                                    whiteSpace: 'nowrap',
                                    zIndex: 1 
                                }}
                            >
                                {diffLabel}
                            </div>
                        )}
                    </div>

                    <div className="d-flex align-items-baseline">
                        <span className="txt-main fw-bold" style={{ fontSize: '1.6rem' }}>
                            {minutesToHM(totalWorked)}
                        </span>
                        <span className="ms-2 fw-semibold txt-subtle" style={{ fontSize: '1rem' }}>
                            / {minutesToHM(totalExpected)} {t('components.WorkedHours.planned')}
                        </span>
                        
                        {totalExpected > 0 && (
                            <span
                                className="ms-3 fw-bold small"
                                style={{ color: isAhead ? 'var(--success-green)' : 'var(--danger-red)' }}
                            >
                                ({diffLabel})
                            </span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default WorkedHours;